// Updated EventAttendeesModal.js using getCommunityUsers consistently
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import AttendeesTab from './AttendeesTab';
import { fetchUsers } from '../../../../services/userService';
import { fetchEventById } from '../../../../services/eventService';
import { getCommunityUsers } from '../../../../services/communityService';
import { addEventAttendees, removeEventAttendee } from '../../../../services/attendanceService';
import { useLanguage } from '../../../../contexts/LanguageContext';
import '../../../MobileModals.css';

function EventAttendeesModal({ event, onClose, onSave }) {
    const { t } = useLanguage();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [modalRoot, setModalRoot] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    // Attendees management
    const [availableUsers, setAvailableUsers] = useState([]);
    const [existingAttendees, setExistingAttendees] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState("");

    // Check for mobile device on resize and mount
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Find or create the modal root element
    useEffect(() => {
        let root = document.getElementById('modal-root');
        if (!root) {
            root = document.createElement('div');
            root.id = 'modal-root';
            document.body.appendChild(root);
        }
        setModalRoot(root);

        // Clean up function
        return () => {
            if (root && root.childNodes.length === 0) {
                document.body.removeChild(root);
            }
        };
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // Get the community ID directly from the event
                const communityId = event.community_id;
                
                // Fetch users
                let users = [];
                
                // If event has a community, use getCommunityUsers - this is the preferred method
                if (communityId) {
                    try {
                        users = await getCommunityUsers(communityId);
                    } catch (err) {
                        setError(t('events.messages.errorFetchingUsers'));
                    }
                } else {
                    // For events without a community, fetch all users without a community
                    users = await fetchUsers();
                    // Filter to only include users without a community_id
                    users = users.filter(user => !user.community_id);
                }
                
                // Get event details with attendees
                const eventData = await fetchEventById(event.id, { includeAttendees: true });
                
                setAvailableUsers(users || []);
                setExistingAttendees(eventData.attendees || []);
            } catch (error) {
                setError(t('events.messages.errorLoadingAttendees'));
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [event.id, event.community_id]);

    const handleAddAttendees = async (selectedUserIds) => {
        if (!selectedUserIds.length) return;
        
        try {
            setSaving(true);
            setError('');
            
            await addEventAttendees(event.id, selectedUserIds);
            
            // Refresh attendees list
            const updatedEvent = await fetchEventById(event.id, { includeAttendees: true });
            setExistingAttendees(updatedEvent.attendees || []);
            setSelectedUsers([]);
        } catch (error) {
            setError(t('events.messages.errorAddingAttendees'));
        } finally {
            setSaving(false);
        }
    };
    
    const handleRemoveAttendee = async (userId) => {
        try {
            setSaving(true);
            setError('');
            
            await removeEventAttendee(event.id, userId);
            
            // Refresh attendees list
            const updatedEvent = await fetchEventById(event.id, { includeAttendees: true });
            setExistingAttendees(updatedEvent.attendees || []);
        } catch (error) {
            setError(t('events.messages.errorRemovingAttendee'));
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => {
        // We've already been updating as we go, so just need to call onSave
        if (typeof onSave === 'function') {
            onSave();
        }
    };

    // Trap focus within modal for accessibility
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        
        // Add body class to prevent scrolling
        document.body.classList.add('modal-open');
        
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.classList.remove('modal-open');
        };
    }, [onClose]);

    // If modal root is not available yet, don't render anything
    if (!modalRoot) return null;

    const getModalWidth = () => {
        if (isMobile) return '100%';
        
        // For desktop, use 2/3 of the viewport width, but capped at 1200px
        const viewportWidth = window.innerWidth;
        const twoThirdsWidth = Math.floor(viewportWidth * 0.66);
        return Math.min(twoThirdsWidth, 1200) + 'px';
    };

    // Use createPortal to render the modal outside the component hierarchy
    return createPortal(
        <div className="modal-overlay">
             <div className="modal-container" style={{ maxWidth: getModalWidth() }}>
                <div className="card border-0 h-100 d-flex flex-column">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                        <h5 className="card-title mb-0" style={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                            {t('events.modals.attendees.title')}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="card-body p-2 p-md-3 overflow-auto" style={{ maxHeight: isMobile ? 'calc(85vh - 120px)' : '500px' }}>
                        {error && <div className="alert alert-danger py-2 px-3 mb-2" style={{ fontSize: isMobile ? '0.85rem' : '1rem' }}>{error}</div>}
                        
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">{t('navigation.loading')}</span>
                                </div>
                            </div>
                        ) : (
                            <AttendeesTab 
                                availableUsers={availableUsers}
                                existingAttendees={existingAttendees}
                                selectedUsers={selectedUsers}
                                setSelectedUsers={setSelectedUsers}
                                userSearchTerm={userSearchTerm}
                                setUserSearchTerm={setUserSearchTerm}
                                onAddAttendees={handleAddAttendees}
                                onRemoveAttendee={handleRemoveAttendee}
                                communityId={event.community_id} 
                                isMobile={isMobile}
                            />
                        )}
                    </div>

                    <div className="card-footer bg-white d-flex justify-content-end py-2">
                        <button
                            type="button"
                            className={`btn btn-secondary me-2 ${isMobile ? 'btn-sm' : ''}`}
                            onClick={onClose}
                            disabled={saving}
                        >
                            {t('events.modals.attendees.close')}
                        </button>
                        <button
                            type="button"
                            className={`btn btn-primary ${isMobile ? 'btn-sm' : ''}`}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {t('events.modals.attendees.done')}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        modalRoot
    );
}

export default EventAttendeesModal;